import uniqBy from 'lodash.uniqby'
import {
  batchEditRectangle,
  batchMoveSingleShareToNameSurn,
  createFieldValue,
  createStandardizedFieldValue,
  dispatchSingleSuper,
  getMostRecentFieldValue,
  getMostRecentFieldValueInArray,
  getNestedOrFloatingElementById,
  getPersonIdsForField,
  INDEXED_INCORRECTLY,
  getSlsTypeByValue,
  getNextUuid,
  batchCreateBasicField,
  compareRect,
} from '@fs/zion-sls'
import { R2AppName } from '../../../../../../shared/services/config'
import { getFieldValueText } from '../../highlightPanel/utils/highlightUtils'

export const TEMPORARY_FIELD_ID = 'TEMPORARY-FIELD-ID' // Used for highlights when creating a new field, replaced with actual field ID on submit

const cloneElement = (element, id = getNextUuid()) => {
  const cloningDispatch = {
    type: 'CREATE_ELEMENT',
    value: { id, partitionKey: element.partitionKey, elementType: element.elementType, extraArgs: {} },
  }
  if (element.elementType === 'FIELD') cloningDispatch.value.extraArgs.fieldType = element.fieldType
  return cloningDispatch
}

export const getPersonsFromField = ({
  currentFieldsOnHighlights,
  selectedPersonId,
  images,
  activeField,
  isNewPerson,
  isNewField,
}) => {
  const sharedElementsCopy = [...currentFieldsOnHighlights]
  if (!isNewField) {
    // Making sure that the selected field is included so that persons sharing it are selectable as well
    const shareableForField = activeField?.typeData?.singleSuper
      ? getNestedOrFloatingElementById(images, activeField?.superElements[0]?.id)
      : activeField
    sharedElementsCopy.unshift(shareableForField)
  }
  let persons = sharedElementsCopy?.reduce((acc, sharedElement) => {
    const personIds = getPersonIdsForField(sharedElement, images)
    const personsFromElement = personIds.map((personId) => getNestedOrFloatingElementById(images, personId))
    acc = acc.concat(...personsFromElement)
    return acc
  }, [])

  if (isNewField && !isNewPerson) persons.unshift(getNestedOrFloatingElementById(images, selectedPersonId)) // WIP
  persons = uniqBy(persons, 'id')
  persons.sort((a, b) => {
    if (a.id === selectedPersonId) return -1
    if (b.id === selectedPersonId) return 1
    return 0
  })

  return persons.flat() || []
}

export const getPersonsByArk = ({ allPersonsSharingField, isNewPerson, selectedPersonId, currentImageArk }) => {
  if (isNewPerson) allPersonsSharingField.unshift({ id: selectedPersonId, partitionKey: currentImageArk })

  return allPersonsSharingField?.reduce(
    (acc, person) => {
      if (!acc[person.partitionKey]) {
        acc[person.partitionKey] = [person]
      } else {
        acc[person.partitionKey].push(person)
      }
      return acc
    },
    {} /* e.g. { '3:1:etc': [...personsOnThisImage] }, '3:1:etc': [...personsOnOtherImage] } */
  )
}

export const getFormDefaultValues = ({ personIds }) => {
  const defaultValues = {}
  personIds.forEach((personId) => {
    defaultValues[personId] = true
  })

  return defaultValues
}

export const getFieldText = (fieldValue) =>
  typeof fieldValue === 'object' ? fieldValue.standard?.standardizedText || fieldValue.originalText : fieldValue

/**
 * Helper functions for creating field type structures
 * These ensure consistency between different parts of the codebase that handle field values
 */

/**
 * Creates SEX_CODE field structure
 * @param {string} text - The text value
 * @param {Object|null} standard - Standard data object with standardizedText and formalText
 * @returns {Object} SEX_CODE structure with primaryText, key, and standard
 */
const createSexCodeStructure = (text, standard) => {
  if (standard) {
    return {
      primaryText: standard.standardizedText || text,
      key: standard.formalText?.split('/').at(-1) || null,
      standard: {
        standardizedText: standard.standardizedText,
        formalText: standard.formalText,
      },
    }
  }
  return {
    primaryText: text,
    key: null,
    standard: null,
  }
}

/**
 * Creates DATE field structure
 * @param {string} text - The text value
 * @param {Object|null} standard - Standard data object with standardizedText and formalText
 * @returns {Object} DATE structure with originalText and standard
 */
const createDateStructure = (text, standard) => {
  if (standard) {
    return {
      originalText: null,
      standard: {
        id: null,
        standardizedText: standard.standardizedText,
        formalText: standard.formalText,
      },
    }
  }
  return {
    originalText: text,
    standard: null,
  }
}

/**
 * Creates PLACE field structure
 * @param {string} text - The text value
 * @param {Object|null} standard - Standard data object with id/formalText and standardizedText
 * @returns {Object} PLACE structure with originalText and standard
 */
const createPlaceStructure = (text, standard) => {
  if (standard) {
    return {
      originalText: null,
      standard: {
        id: standard.id || standard.formalText?.split('/').at(-1),
        standardizedText: standard.standardizedText,
        formalText: null,
      },
    }
  }
  return {
    originalText: text,
    standard: null,
  }
}

/**
 * Transforms linkedFieldValue into the structure expected by the form based on activeFieldType
 * When field types mismatch, returns non-standard version as you can't standardize mismatched data
 *
 * @param {string} activeFieldType - The field type from activeFieldInfo that determines expected structure
 * @param {Object|string} linkedFieldValue - The value from the linked field (may include standard, originalText, etc.)
 * @returns {Object|string} Transformed value matching the expected structure for the active field type
 */
const transformLinkedFieldValueToActiveFieldType = (activeFieldType, linkedFieldValue) => {
  // Extract text content from linkedFieldValue using the shared utility
  const text = typeof linkedFieldValue === 'string' ? linkedFieldValue : getFieldValueText(linkedFieldValue)

  const standard = linkedFieldValue?.value?.standard
  const linkedFieldType = linkedFieldValue?.fieldType

  // Determine if we can use standard data (only when types match and standard exists)
  const canUseStandard = standard && (!linkedFieldType || activeFieldType === linkedFieldType)
  const standardToUse = canUseStandard ? standard : null

  // Transform based on activeFieldType to match expected form structure
  switch (activeFieldType) {
    case 'SEX_CODE':
      return createSexCodeStructure(text, standardToUse)

    case 'DATE':
      return createDateStructure(text, standardToUse)

    case 'PLACE':
      return createPlaceStructure(text, standardToUse)

    default:
      return standardToUse ? standardToUse.standardizedText || text : text
  }
}

/**
 * Creates a field value for SLS dispatches with proper handling of standard values
 * Determines whether to use createFieldValue or createStandardizedFieldValue based on the data
 *
 * @param {Object} params - Parameters object
 * @param {Object|string} params.field - The selected field value which may contain standard data or just text.
 * @param {string} params.fieldType - The field type to use for standardization
 * @param {Object} params.attribution - Attribution object containing comment, reasonId, and app
 * @returns {Object} Field value ready for SLS dispatch
 */
const createFieldValueForDispatch = ({ field, fieldType, attribution }) => {
  const hasStandardValue = field?.value?.standard

  if (hasStandardValue) {
    return createStandardizedFieldValue(field.value, fieldType, attribution)
  }

  // Extract text from the field
  const text = typeof field === 'object' ? field.originalText || field?.displayValue : field

  return createFieldValue(text, attribution)
}

/**
 * Two tasks:
 * Update the field id of ADD_RECT dispatches from the useSlsHighlights hook, adding any rects that are part of the current edit
 * Move all rects already saved on fieldInEdit to the new field
 * @param {Array} originalHighlightDispatches - dispatches generated by the useSlsHighlights hook
 * @param {Object} fieldInfoForDispatches - id and partitionKey of new linked field being created
 * @param {Object} fieldInEdit - the field that was edited with the highlight panel
 * @returns {*[]}
 */
function addUpdatedHighlightDispatches(originalHighlightDispatches, fieldInfoForDispatches, fieldInEdit) {
  // Step 1: Replace temporary field IDs with the actual field ID
  // The useSlsHighlights hook doesn't have access to the newly created field ID, so we update it here
  const highlightDispatches = originalHighlightDispatches.map((dispatch) => {
    if (dispatch.type === 'ADD_RECT' && dispatch.value.id === TEMPORARY_FIELD_ID) {
      return {
        ...dispatch,
        value: { ...dispatch.value, id: fieldInfoForDispatches.id },
      }
    }
    return dispatch
  })

  // Step 2: Move existing rects from the old field to the new field (excluding removed rects)
  const removeRectDispatchesForFieldInEdit = highlightDispatches.filter(
    (d) => d.type === 'REM_RECT' && d.value.id === fieldInEdit.id
  )

  fieldInEdit?.rects?.forEach((rect) => {
    const wasRectRemoved = removeRectDispatchesForFieldInEdit.some((d) => compareRect(d.value.rect, rect))

    if (!wasRectRemoved) {
      highlightDispatches.push(
        ...batchEditRectangle({
          oldFieldId: fieldInEdit.id,
          fieldId: fieldInfoForDispatches.id,
          rect,
          oldRect: rect,
          partitionKey: fieldInfoForDispatches.partitionKey,
          oldPartitionKey: fieldInEdit.partitionKey,
        })
      )
    }
  })

  return highlightDispatches
}

/**
 * Clones field and creates dispatches for field value update
 * Handles both regular fields and fields with singleSuper elements
 *
 * @param {Object} params - Parameters object
 * @param {Object} params.fieldInEdit - The field being edited
 * @param {Object} params.linkedFieldValue - The new field value to set
 * @param {Object} params.activeFieldInfo - Information about the active field
 * @param {Object} params.images - Images from state
 * @param {Array} params.sharedElements - Array to track shared elements
 * @param {Object} params.partitionKey - Optional partition key for new field
 *
 * @returns {Object} Object containing dispatches, clonedField, and subElementInfoForDispatches
 */
function createCloneAndFieldValueDispatches({
  fieldInEdit,
  linkedFieldValue,
  activeFieldInfo,
  images,
  sharedElements,
  partitionKey,
  types,
}) {
  const dispatches = []
  // If the field is new we can use its existing id for the clone, otherwise we need to generate a new id
  const elementId = fieldInEdit?.added ? fieldInEdit.id : getNextUuid()

  // Clone the field
  dispatches.push(cloneElement({ ...fieldInEdit, ...(partitionKey && { partitionKey }) }, elementId))
  const clonedField = dispatches[0].value

  // Create field value with attribution
  const attribution = {
    comment: '',
    reasonId: INDEXED_INCORRECTLY,
    app: R2AppName,
  }

  const fieldValue = createFieldValueForDispatch({
    field: linkedFieldValue,
    fieldType: activeFieldInfo.fieldType,
    attribution,
  })
  dispatches.push({
    type: 'ADD_FV',
    value: {
      fieldValue,
      id: clonedField.id,
      partitionKey: clonedField.partitionKey,
    },
  })

  // Determine the shareable element (could be the field itself or its parent)
  let subElementInfoForDispatches = clonedField
  const typeDataForFieldInEdit =
    getSlsTypeByValue(fieldInEdit?.fieldType, 'fields', types) ||
    getSlsTypeByValue(fieldInEdit?.elementType, 'elements', types)

  if (typeDataForFieldInEdit?.singleSuper) {
    const shareableElementForField = getNestedOrFloatingElementById(images, fieldInEdit?.superElements?.[0]?.id)
    dispatches.push(cloneElement(shareableElementForField))
    subElementInfoForDispatches = dispatches[2].value
    dispatches.push({
      type: 'ADD_SUB',
      value: {
        superPartitionKey: subElementInfoForDispatches.partitionKey,
        subPartitionKey: clonedField.partitionKey,
        superId: subElementInfoForDispatches.id,
        subId: clonedField.id,
        elementType: subElementInfoForDispatches.elementType,
      },
    })
    if (!sharedElements.includes(shareableElementForField)) {
      sharedElements.push(shareableElementForField)
    }
  } else if (!sharedElements.includes(fieldInEdit)) {
    sharedElements.push(fieldInEdit)
  }

  return {
    dispatches,
    clonedField,
    subElementInfoForDispatches,
  }
}

/**
 * Creates dispatches to move shared elements between persons
 * Handles NAME_SURNAME special case and regular person selection
 *
 * @param {Object} params - Parameters object
 * @param {Array} params.sharedElements - Elements being shared
 * @param {Array} params.selectedPersonIds - IDs of selected persons
 * @param {Object} params.subElementInfoForDispatches - Sub element info for dispatches
 * @param {Object} params.images - Images from state
 *
 * @returns {Array} Array of dispatches for moving elements
 */
function createMoveSharedElementsDispatches({
  sharedElements,
  selectedPersonIds,
  subElementInfoForDispatches,
  images,
}) {
  const dispatches = []

  sharedElements.forEach((sharedElement) => {
    // On each superElement of the sharedElement, replace the sharedElement with the fieldInEdit if the associated person is selected
    if (sharedElement?.superElements) {
      sharedElement.superElements.forEach((superElement) => {
        const expandedSuperElement = getNestedOrFloatingElementById(images, superElement.id)
        // We only need to move the subelement if the associated person has been selected
        if (expandedSuperElement?.superElements) {
          if (expandedSuperElement?.elementType === 'NAME_SURNAME' && expandedSuperElement.superElements.length > 1) {
            expandedSuperElement?.superElements.forEach((nameSuperElement) => {
              // Trying to share at the NAME_SURN level, but this element shares at the NAME_SURNAME level, so we will change it
              dispatches.push(
                ...batchMoveSingleShareToNameSurn(nameSuperElement, expandedSuperElement, subElementInfoForDispatches)
              )
            })
          } else if (selectedPersonIds.includes(expandedSuperElement?.personId)) {
            // Create a dispatch to remove the sharedElement from its superElement
            dispatches.push({
              type: 'REM_SUB',
              value: {
                superPartitionKey: expandedSuperElement.partitionKey,
                subPartitionKey: sharedElement.partitionKey,
                superId: expandedSuperElement.id,
                subId: sharedElement.id,
                elementType: expandedSuperElement.elementType,
              },
            })
            // Create a dispatch to add the fieldInEdit to the superElement
            dispatches.push({
              type: 'ADD_SUB',
              value: {
                superPartitionKey: expandedSuperElement.partitionKey,
                subPartitionKey: subElementInfoForDispatches.partitionKey,
                superId: expandedSuperElement.id,
                subId: subElementInfoForDispatches.id,
                elementType: expandedSuperElement.elementType,
              },
            })
          }
        }
      })
    }
  })

  return dispatches
}

/**
 * Extracts selected and deselected person IDs from form values
 * Form keys are person IDs (starting with '1:1:'), values are booleans
 *
 * @param {Object} values - Form values with person IDs as keys
 * @returns {Object} Object with selectedPersonIds and deselectedPersonIds arrays
 */
const getSelectedPersonIdsFromValues = (values) => {
  const selectedPersonIds = []
  const deselectedPersonIds = []

  Object.entries(values)
    .filter(([key]) => key.startsWith('1:1:'))
    .forEach(([key, value]) => {
      if (value) selectedPersonIds.push(key)
      else deselectedPersonIds.push(key)
    })

  return { selectedPersonIds, deselectedPersonIds }
}

/**
 * Creates dispatches to clear localized names for selected persons to force a refresh
 *
 * @param {Object} params - Parameters object
 * @param {Array} params.selectedPersonIds - IDs of selected persons
 * @param {string} params.selectedPersonId - The currently selected person ID
 * @param {Array} params.involvedPersons - Array of involved persons
 *
 * @returns {Array} Array of SET_PERSON_LOCALIZED_NAME dispatches
 */
function createLocalizeNameDispatches({ selectedPersonIds, selectedPersonId, involvedPersons }) {
  const uniqueSelectedPersonIds = [...new Set([...selectedPersonIds, selectedPersonId].filter(Boolean))]
  return uniqueSelectedPersonIds.map((personId) => {
    const person = involvedPersons?.find((p) => p.id === personId)
    return {
      type: 'SET_PERSON_LOCALIZED_NAME',
      value: {
        personId,
        localizedName: '',
        partitionKey: person?.partitionKey,
      },
    }
  })
}

/**
 * Creates dispatches for a completely new field with highlights
 * Handles two scenarios:
 * 1. Linking to an existing field value from another person
 * 2. Creating a brand new field value
 *
 * @param {Object} params - Parameters object
 * @param {Object} params.personIdValues - Form values with selected person IDs
 * @param {Object} params.selectedField - The field value being linked
 * @param {Object} params.activeField - The field template/info being edited
 * @param {string} params.partitionKey - Partition key (image ARK) for the new field
 * @param {Array} params.originalHighlightDispatches - Highlight rectangle dispatches
 * @param {Object} params.images - Images from state
 * @param {Array} params.sharedElements - Shared elements tracker
 * @param {string} params.selectedPersonId - Currently selected person ID
 * @param {Array} params.involvedPersons - All persons involved
 * @param {Object} params.types - SLS type definitions
 * @param {boolean} params.isNewPerson - Whether the new field is being created as part of a new person creation
 *
 * @returns {Object} Dispatch data with dispatches array, newFieldId, selectedPersonIds, etc.
 */
export const createDispatchesForNewField = ({
  personIdValues,
  selectedField,
  activeField,
  partitionKey,
  originalHighlightDispatches,
  images,
  sharedElements,
  selectedPersonId,
  involvedPersons,
  types,
  isNewPerson = false,
}) => {
  const dispatches = []
  const { selectedPersonIds } = getSelectedPersonIdsFromValues(personIdValues)
  const isCreatingNewFieldValue = selectedField?.added ?? false
  const newFieldId = activeField?.id || getNextUuid()

  let fieldInfoForDispatches = {}
  let subElementInfoForDispatches = {}
  let clonedField = {}

  // SCENARIO 1: Linking existing field value from another person
  // Clone the field structure and copy the existing value
  if (!isCreatingNewFieldValue) {
    const cloneResult = createCloneAndFieldValueDispatches({
      fieldInEdit: activeField,
      linkedFieldValue: selectedField,
      activeFieldInfo: activeField,
      images,
      sharedElements,
      partitionKey,
      types,
    })

    dispatches.push(...cloneResult.dispatches)
    clonedField = cloneResult.clonedField
    subElementInfoForDispatches = cloneResult.subElementInfoForDispatches
    fieldInfoForDispatches = clonedField
  }
  // SCENARIO 2: Creating completely new field value
  // Use batchCreateBasicField to create all necessary structures
  else {
    const attribution = {
      comment: '',
      reasonId: INDEXED_INCORRECTLY,
      app: R2AppName,
    }

    const fieldValueTypeData = getSlsTypeByValue(activeField.fieldType, 'fields', types)
    const fieldValue = createFieldValueForDispatch({
      field: selectedField,
      fieldType: activeField.fieldType,
      attribution,
    })

    const newFieldDispatches = batchCreateBasicField({
      partitionKey,
      superId: activeField?.elementId || activeField?.superElements?.[0].id,
      fieldType: activeField?.fieldType,
      fieldValue,
      superElementType: fieldValueTypeData?.elementTypes?.[0],
      id: newFieldId,
    })

    dispatches.push(...newFieldDispatches)

    // Extract the shareable element info from the created dispatches
    fieldInfoForDispatches = { id: activeField?.id }
    const createFieldDispatch = dispatches.find((d) => d?.value?.elementType === 'FIELD')

    if (dispatchSingleSuper(createFieldDispatch)) {
      const addSubDispatch = dispatches.find((d) => d.type === 'ADD_SUB' && d.value.subId === fieldInfoForDispatches.id)
      const createSuperDispatch = dispatches.find(
        (d) => d.type === 'CREATE_ELEMENT' && d.value.id === addSubDispatch.value.superId
      )
      subElementInfoForDispatches = createSuperDispatch
        ? createSuperDispatch.value
        : getNestedOrFloatingElementById(images, addSubDispatch.value.superId)
    } else {
      subElementInfoForDispatches = createFieldDispatch.value
    }
  }

  // Common operations for both scenarios:
  // 1. Add highlight rectangles to the new field
  dispatches.push(...addUpdatedHighlightDispatches(originalHighlightDispatches, fieldInfoForDispatches, activeField))

  // 2. Move shared elements to selected persons
  dispatches.push(
    ...createMoveSharedElementsDispatches({
      sharedElements,
      selectedPersonIds,
      subElementInfoForDispatches,
      images,
    })
  )

  // 3. Clear localized names to force refresh
  dispatches.push(
    ...createLocalizeNameDispatches({
      selectedPersonIds: selectedPersonIds.filter((id) => id !== selectedPersonId),
      selectedPersonId: isNewPerson ? undefined : selectedPersonId,
      involvedPersons,
    })
  )

  return {
    dispatches,
    newFieldId: clonedField?.id || newFieldId,
    selectedPersonIds,
    creatingNewField: isCreatingNewFieldValue,
    newFieldValue: transformLinkedFieldValueToActiveFieldType(activeField.fieldType, selectedField),
  }
}

/**
 * Extracts shareable element info from field dispatches
 * Handles both single-super (field with parent) and direct field cases
 *
 * @param {Array} dispatches - Array of dispatches to search through
 * @param {Object} fieldInfoForDispatches - Field info with id
 * @param {Object} images - Images from state
 * @returns {Object} Shareable element info (super element or field itself)
 */
const extractShareableElementInfo = (dispatches, fieldInfoForDispatches, images) => {
  const createFieldDispatch = dispatches.find((d) => d?.value?.elementType === 'FIELD')

  if (dispatchSingleSuper(createFieldDispatch)) {
    const addSubDispatch = dispatches.find((d) => d.type === 'ADD_SUB' && d.value.subId === fieldInfoForDispatches.id)
    const createSuperDispatch = dispatches.find(
      (d) => d.type === 'CREATE_ELEMENT' && d.value.id === addSubDispatch.value.superId
    )
    return createSuperDispatch
      ? createSuperDispatch.value
      : getNestedOrFloatingElementById(images, addSubDispatch.value.superId)
  }

  return createFieldDispatch.value
}

/**
 * Handles linking fields to persons via highlights for existing fields
 * Modifies field associations and updates highlight connections
 *
 * This function is specifically for updating EXISTING fields with new person associations.
 * For creating NEW fields, use createDispatchesForNewField instead.
 *
 * @param {Object} params - Parameters object
 * @param {Object} params.values - Form values with selected person IDs
 * @param {Object} params.linkedFieldValue - The field value being linked
 * @param {Object} params.activeFieldInfo - Active field info
 * @param {boolean} params.isNewField - Whether this is a new field (should always be false here)
 * @param {Array} params.originalDispatches - Original dispatches from previous panel
 * @param {Array} params.originalHighlightDispatches - Highlight rectangle dispatches
 * @param {Object} params.types - SLS type definitions
 * @param {Object} params.images - Images from state
 * @param {Object} params.fieldInEdit - The field being edited
 * @param {Array} params.sharedElements - Shared elements tracker
 * @param {string} params.selectedPersonId - Currently selected person ID
 * @param {Array} params.involvedPersons - All persons involved
 *
 * @returns {Object} Dispatch data with dispatches array, newFieldId, selectedPersonIds, etc.
 */
export const handleSubmitLinkFieldsViaHighlights = ({
  values,
  linkedFieldValue,
  activeFieldInfo,
  isNewField,
  originalDispatches,
  originalHighlightDispatches,
  types,
  images,
  fieldInEdit,
  sharedElements,
  selectedPersonId,
  involvedPersons,
}) => {
  const { selectedPersonIds } = getSelectedPersonIdsFromValues(values)
  let dispatches = []
  let fieldInfoForDispatches = {}
  let subElementInfoForDispatches = {}

  // SCENARIO 1: New field (uncommon - usually handled by createDispatchesForNewField)
  // Reuse dispatches from previous panel and update the field value
  if (isNewField) {
    dispatches = [...originalDispatches]

    // Update the field value in the existing ADD_FV dispatch
    const addFVDispatch = dispatches.find((d) => d.type === 'ADD_FV')
    const existingAttribution = addFVDispatch.value.fieldValue.attribution

    addFVDispatch.value.fieldValue = createFieldValueForDispatch({
      field: linkedFieldValue,
      fieldType: activeFieldInfo.fieldType,
      attribution: existingAttribution,
    })

    fieldInfoForDispatches = { id: activeFieldInfo?.id }
    subElementInfoForDispatches = extractShareableElementInfo(dispatches, fieldInfoForDispatches, images)
  }
  // SCENARIO 2: Existing field (most common case)
  // Clone the field and create new associations
  else {
    const cloneResult = createCloneAndFieldValueDispatches({
      fieldInEdit,
      linkedFieldValue,
      activeFieldInfo,
      images,
      sharedElements,
      types,
    })

    dispatches.push(...cloneResult.dispatches)
    subElementInfoForDispatches = cloneResult.subElementInfoForDispatches
    fieldInfoForDispatches = cloneResult.clonedField
  }

  // Common operations for both scenarios:
  // 1. Add highlight rectangles to the field
  dispatches.push(...addUpdatedHighlightDispatches(originalHighlightDispatches, fieldInfoForDispatches, fieldInEdit))

  // 2. Move shared elements to selected persons
  dispatches.push(
    ...createMoveSharedElementsDispatches({
      sharedElements,
      selectedPersonIds,
      subElementInfoForDispatches,
      images,
    })
  )

  // 3. Clear localized names to force refresh
  dispatches.push(
    ...createLocalizeNameDispatches({
      selectedPersonIds,
      selectedPersonId,
      involvedPersons,
    })
  )

  return {
    dispatches,
    newFieldId: fieldInfoForDispatches.id,
    removedFieldIds: sharedElements.map((el) => el.id),
    newFieldValue: transformLinkedFieldValueToActiveFieldType(activeFieldInfo.fieldType, linkedFieldValue),
    selectedPersonIds,
  }
}

/**
 * Helper function to get field value using the same logic as SelectSlsField
 * Extracts field values from different field structures consistently
 * @param {Object} field - The field object to extract value from
 * @param {Object} state - The SLS state context containing type information
 * @returns {string} The extracted field value or empty string
 */
export const getFieldValue = (field, state) => {
  let result = ''

  if (field?.value) {
    if (field.value?.origValue?.text) {
      result = field.value.origValue.text
    } else if (field.value?.primaryText) {
      result = field.value.primaryText
    } else if (field.value.value) {
      result = field.value.value // From a useFieldArray managed field
    } else if (!state.types?.fields.find((type) => type.fieldType === field.value)) {
      result = field.value
    }
  } else if (field?.fieldValues?.[0]?.origValue?.text) {
    result = field.fieldValues[0].origValue.text
  } else {
    result = getMostRecentFieldValue(field) || ''
  }

  return result
}

/**
 * Filters highlights on the indicated image down to only those related to the currently selected field
 *
 * @param {String} imageArk The ark id of the image on which we need highlights
 * @param {Array} editHighlights The array of highlights in state that we're filtering down
 * @param {String} selectedPersonId The currently selected person ID
 * @param {String} fieldType The field type to filter by
 *
 * @returns {Array} An array of highlights on the image that relate to the currently selected field
 */
export const getHighlightsForField = (imageArk, editHighlights, selectedPersonId, fieldType) => {
  return editHighlights
    .filter((highlight) => highlight.personIds.includes(selectedPersonId))
    .filter((f) => fieldType === f.fieldType)
    .filter((h) => h?.imageId === imageArk)
}

const getFieldValueObject = (field) => {
  const { fieldType } = field || {}
  const mostRecentFieldValue = getMostRecentFieldValueInArray(field)
  const text = mostRecentFieldValue?.origValue?.text || ''

  // Normalize the standard data structure from SLS format to our helper format
  const normalizedValue = mostRecentFieldValue?.normalizedValues?.[0]
  const standard = normalizedValue
    ? {
        standardizedText: normalizedValue.text,
        formalText: normalizedValue.resource,
        id: normalizedValue.resource?.split('/').at(-1),
      }
    : null

  switch (fieldType) {
    case 'SEX_CODE':
      return createSexCodeStructure(text, standard)

    case 'DATE':
      return createDateStructure(text, standard)

    case 'PLACE':
      return createPlaceStructure(text, standard)

    default:
      return standard?.standardizedText || text
  }
}

/**
 * Creates field info list for select components from highlights
 *
 * @param {Object} params - Parameters object
 * @param {Array} params.highlightsOnSelectedField - Highlights on the current field
 * @param {Array} params.splitScreenHighlightsOnSelectedField - Highlights on split screen field
 * @param {Object} params.newFieldForHighlight - The new field for highlight
 * @param {Array} params.images - Images from state
 * @param {Array} params.fieldTypes - Field types from state
 * @param {Object} params.state - SLS state context
 *
 * @returns {Array} Field info array for select lists
 */
export const createFieldInfoForSelectList = ({
  highlightsOnSelectedField,
  splitScreenHighlightsOnSelectedField,
  newFieldForHighlight,
  images,
  fieldTypes,
  state,
}) => {
  // We initialize an empty array and populate existing fields first to prioritize them over new duplicates
  const fieldInfo = []

  // This helper function generates objects representing the information we need from the fields on the highlights
  const getFieldInfoFromHighlights = (highlights) => {
    return highlights.forEach((h) => {
      if (h?.originalHighlight?.personIds?.length > 0) {
        const fieldOnHighlight = getNestedOrFloatingElementById(images, h?.originalHighlight?.fieldId)
        const displayValue = getFieldValueText(fieldOnHighlight)
        const value = getFieldValueObject(fieldOnHighlight)

        const alreadyHasInfo = fieldInfo.find(
          (f) =>
            f.id === h?.originalHighlight?.fieldId ||
            (f.fieldType === h.originalHighlight?.fieldType && f.displayValue === displayValue)
        )

        if (!alreadyHasInfo) {
          const info = fieldOnHighlight
            ? {
                ...fieldOnHighlight,
                value,
                displayValue,
                isFromSlsData: true,
              }
            : {
                id: h?.originalHighlight?.fieldId,
                elementType: 'FIELD',
                fieldType: h?.originalHighlights?.fieldType,
                label: fieldTypes?.find((type) => type.fieldType === h?.originalHighlight?.fieldType)?.displayLabel,
                isFromSlsData: true,
              }
          fieldInfo.push(info)
        }
      }
    })
  }

  getFieldInfoFromHighlights(highlightsOnSelectedField)
  getFieldInfoFromHighlights(splitScreenHighlightsOnSelectedField)

  // Add the new field only if it doesn't match an existing field with the same fieldType and displayValue
  const newFieldDisplayValue =
    newFieldForHighlight?.displayValue ||
    (typeof newFieldForHighlight?.value === 'string'
      ? newFieldForHighlight.value
      : getFieldValueText(newFieldForHighlight))

  const matchingExistingField = fieldInfo.find(
    (f) =>
      f.id === newFieldForHighlight?.id ||
      (f.fieldType === newFieldForHighlight?.fieldType && f.displayValue === newFieldDisplayValue)
  )

  if (!matchingExistingField) {
    fieldInfo.push({
      ...newFieldForHighlight,
      // We prioritize the value coming from the form for consistency with the SelectSlsField display,
      // but if it's not available we use getFieldValue to extract it from the field object
      value: newFieldForHighlight?.value || getFieldValue(newFieldForHighlight, state),
      displayLabel: newFieldForHighlight?.displayLabel || newFieldForHighlight?.label,
      displayValue: newFieldDisplayValue,
      standard: newFieldForHighlight?.value?.standard || newFieldForHighlight?.value?.normalizedValues?.[0] || null,
    })
  }

  return fieldInfo.reverse() // Reverse to prioritize existing fields over the new field in the display order
}

/**
 * Gets pluralized instructions text for field selection
 *
 * @param {Object} params - Parameters object
 * @param {Array} params.fieldInfoForSelectList - Field info list
 * @param {Object} params.newFieldForHighlight - New field for highlight
 * @param {boolean} params.isReassignFlow - Whether this is a reassign flow
 * @param {Function} params.t - Translation function
 *
 * @returns {Object} Object with header and instructions text
 */
export const getPluralizationText = ({ fieldInfoForSelectList, newFieldForHighlight, isReassignFlow, t }) => {
  let header
  let instructions

  if (isReassignFlow) {
    header = t(
      'review-highlight-linked-fields.select-field-instructions.header-reassign',
      'Select the correct transcription'
    )
    instructions = t(
      'review-highlight-linked-fields.select-field-instructions.text-reassign',
      'The selected highlight can only be linked to one field at a time. Pick the transcription to use.'
    )
  } else if (fieldInfoForSelectList.length > 1) {
    header = t(
      'review-highlight-linked-fields.select-field-instructions.header-plural',
      'Highlights for {fieldType} fields.',
      { fieldType: newFieldForHighlight.label }
    )
    instructions = t(
      'review-highlight-linked-fields.select-field-instructions.text-plural',
      'The selected highlights are linked with the fields below. Pick the transcription to use.'
    )
  } else {
    header = t(
      'review-highlight-linked-fields.select-field-instructions.header-singular',
      'Highlight for {fieldType} field.',
      { fieldType: newFieldForHighlight.label }
    )
    instructions = t(
      'review-highlight-linked-fields.select-field-instructions.text-singular',
      'The selected highlight is linked with the fields below. Pick the transcription to use.'
    )
  }

  return { header, instructions }
}
